// Every query and mutation of the Workspace screens. Keys are written here only.
//
// - What is the person's across workspaces lives under ["team", ...] (the list
//   of workspaces, an invite, the slug check, my Cases that link to a thing).
// - What belongs to one workspace lives under ["ws", slug, ...], so a team
//   event of that workspace can drop just its part (`wsPrefix(slug)`).
// Writes show at once (`optimisticMutation`) and roll back on failure; what
// they create carries an Idempotency-Key, the same for retries.
import {
  type QueryClient,
  queryOptions,
  useMutation,
  useMutationState,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  InvitePublicView,
  InviteView,
  MemberKind,
  MemberView,
  RoleView,
  SpaceMemberView,
  SpaceView,
  SpaceVisibility,
  WorkspaceSummary,
  WorkspaceView,
} from "@trameria/cloud-api/workspace-shared";
import {
  type Api,
  idempotencyKeyFor,
  optimisticMutation,
  useApi,
} from "@trameria/ui-core";

const enc = encodeURIComponent;
const ws = (slug: string) => `/api/w/${enc(slug)}`;

// ---- across workspaces ---------------------------------------------------------

export const teamPrefix = ["team"] as const;
export const workspacesKey = ["team", "workspaces"] as const;

/** The workspaces the person is in, in the order they joined. */
export const workspacesQuery = (api: Api) =>
  queryOptions({
    queryKey: workspacesKey,
    queryFn: async () =>
      (await api<{ workspaces: WorkspaceSummary[] }>("/api/workspaces"))
        .workspaces,
  });

export interface SlugAvailability {
  available: boolean;
  problem: "format" | "reserved" | "taken" | null;
}

export const slugAvailableQuery = (api: Api, slug: string) =>
  queryOptions({
    queryKey: ["team", "slug-available", slug] as const,
    queryFn: () =>
      api<SlugAvailability>(`/api/workspaces/slug-available?slug=${enc(slug)}`),
    staleTime: 10_000,
  });

/** An invite as anyone sees it (no sign-in needed). 404 when there is none. */
export const inviteQuery = (api: Api, token: string) =>
  queryOptions({
    queryKey: ["team", "invite", token] as const,
    queryFn: () => api<InvitePublicView>(`/api/invites/${enc(token)}`),
    retry: false,
  });

/** One of my Cases that links to a thing (from the refs' backlinks). */
export interface MyCaseLink {
  id: string;
  label: string;
}

export const myCasesPrefix = ["team", "my-cases"] as const;

/**
 * My Cases that link to a thing. Backlinks list only what the viewer may see,
 * and Cases are only ever their owner's, so these are mine.
 */
export const myCasesQuery = (api: Api, kind: string, id: string) =>
  queryOptions({
    queryKey: [...myCasesPrefix, kind, id] as const,
    queryFn: async (): Promise<MyCaseLink[]> => {
      const { backlinks } = await api<{
        backlinks: Array<{ kind: string; id: string; label: string }>;
      }>(`/api/refs/backlinks?kind=${enc(kind)}&id=${enc(id)}`);
      return backlinks
        .filter((b) => b.kind === "case")
        .map((b) => ({ id: b.id, label: b.label }));
    },
  });

export function useCreateWorkspace() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; slug: string }) =>
      api<WorkspaceSummary>("/api/workspaces", {
        method: "POST",
        body: input,
        idempotencyKey: idempotencyKeyFor(input),
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: workspacesKey }),
  });
}

export function useAcceptInvite() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { token: string }) =>
      api<{ workspace: WorkspaceSummary; joined: boolean }>(
        `/api/invites/${enc(input.token)}/accept`,
        { method: "POST", idempotencyKey: idempotencyKeyFor(input) },
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: teamPrefix }),
  });
}

// ---- one workspace -------------------------------------------------------------

export const wsPrefix = (slug: string) => ["ws", slug] as const;
export const workspaceViewKey = (slug: string) => ["ws", slug, "view"] as const;

/** The workspace as the person sees it: permissions, kind, features, profile. */
export const workspaceViewQuery = (api: Api, slug: string) =>
  queryOptions({
    queryKey: workspaceViewKey(slug),
    queryFn: () => api<WorkspaceView>(ws(slug)),
  });

/** Writes that answer the whole view put it into the cache as it is. */
function useViewWrite<TInput>(
  slug: string,
  send: (input: TInput) => Promise<WorkspaceView>,
  apply: (old: WorkspaceView, input: TInput) => WorkspaceView,
) {
  const qc = useQueryClient();
  const key = workspaceViewKey(slug);
  return useMutation({
    mutationFn: send,
    ...optimisticMutation<WorkspaceView, TInput>(qc, key, (old, input) =>
      old ? apply(old, input) : old,
    ),
    onSuccess: (view: WorkspaceView) => {
      qc.setQueryData(key, view);
      void qc.invalidateQueries({ queryKey: workspacesKey });
    },
  });
}

/** Renames the workspace, moves its URL (the screen then goes to the new URL) or sets its icon. */
export function useUpdateWorkspace(slug: string) {
  const api = useApi();
  return useViewWrite<{
    name?: string;
    slug?: string;
    iconFileId?: string | null;
  }>(
    slug,
    (input) => api<WorkspaceView>(ws(slug), { method: "PATCH", body: input }),
    (old, input) => ({
      ...old,
      ...(input.name ? { name: input.name } : {}),
      ...(input.iconFileId !== undefined
        ? { iconFileId: input.iconFileId }
        : {}),
    }),
  );
}

export interface FeaturesInput {
  /** Every workspace feature that is on (not a change). */
  features?: string[];
  mcpAllowed?: boolean;
  publishAllowed?: boolean;
}

export function useUpdateFeatures(slug: string) {
  const api = useApi();
  return useViewWrite<FeaturesInput>(
    slug,
    (input) =>
      api<WorkspaceView>(`${ws(slug)}/features`, {
        method: "PATCH",
        body: input,
      }),
    (old, input) => ({ ...old, ...input }),
  );
}

export function useUpdateMyProfile(slug: string) {
  const api = useApi();
  return useViewWrite<{ displayName?: string; avatarFileId?: string | null }>(
    slug,
    (input) =>
      api<WorkspaceView>(`${ws(slug)}/me`, { method: "PATCH", body: input }),
    (old, { avatarFileId, ...input }) => ({
      ...old,
      me: {
        ...old.me,
        ...input,
        // A picture of its own shows at once; taking it away waits for the
        // answer (the account's then shows).
        ...(avatarFileId
          ? { avatarFileId, workspaceAvatarFileId: avatarFileId }
          : {}),
      },
    }),
  );
}

// ---- members -------------------------------------------------------------------

export const membersKey = (slug: string) => ["ws", slug, "members"] as const;

/** Everyone in the workspace (the server answers up to MEMBER_LIST_MAX). */
export const membersQuery = (api: Api, slug: string) =>
  queryOptions({
    queryKey: membersKey(slug),
    queryFn: async () =>
      (await api<{ members: MemberView[] }>(`${ws(slug)}/members`)).members,
  });

function useMembersWrite<TInput extends { userId: string }>(
  slug: string,
  send: (input: TInput) => Promise<unknown>,
  apply: (member: MemberView, input: TInput) => MemberView | null,
  mutationKey?: readonly unknown[],
) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey,
    mutationFn: send,
    ...optimisticMutation<MemberView[], TInput>(
      qc,
      membersKey(slug),
      (old, input) =>
        old?.flatMap((m) => {
          if (m.userId !== input.userId) return [m];
          const next = apply(m, input);
          return next ? [next] : [];
        }),
    ),
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: membersKey(slug) }),
        // Roles count their members; the view holds my own roles.
        qc.invalidateQueries({ queryKey: rolesKey(slug) }),
        qc.invalidateQueries({ queryKey: workspaceViewKey(slug) }),
      ]),
  });
}

export function useSetMemberRoles(slug: string) {
  const api = useApi();
  return useMembersWrite<{ userId: string; roleIds: string[]; name: string }>(
    slug,
    (input) =>
      api<MemberView>(`${ws(slug)}/members/${enc(input.userId)}/roles`, {
        method: "PUT",
        body: { roleIds: input.roleIds },
      }),
    (member, input) => ({ ...member, roleIds: input.roleIds }),
  );
}

export function useSetMemberKind(slug: string) {
  const api = useApi();
  return useMembersWrite<{ userId: string; kind: MemberKind; name: string }>(
    slug,
    (input) =>
      api<MemberView>(`${ws(slug)}/members/${enc(input.userId)}/kind`, {
        method: "PATCH",
        body: { kind: input.kind },
      }),
    // A guest holds no roles (the server takes them off).
    (member, input) => ({
      ...member,
      kind: input.kind,
      roleIds: input.kind === "guest" ? [] : member.roleIds,
    }),
  );
}

const removeMemberKey = (slug: string) =>
  ["ws", slug, "remove-member"] as const;

/** Removing someone; the screen leaves their detail at once. */
export function useRemoveMember(slug: string) {
  const api = useApi();
  return useMembersWrite<{ userId: string; name: string }>(
    slug,
    (input) =>
      api(`${ws(slug)}/members/${enc(input.userId)}`, { method: "DELETE" }),
    () => null,
    removeMemberKey(slug),
  );
}

export const useRemoveMemberFailure = (slug: string) =>
  useLastFailure<{ name: string }>(removeMemberKey(slug));

/**
 * The failure of the latest call of a mutation whose screen went away when it
 * was pressed (a removal leaves the detail at once): told by the list instead.
 * Null once a later call succeeds or while one runs.
 */
function useLastFailure<TVars>(
  mutationKey: readonly unknown[],
): { error: unknown; name: string } | null {
  const states = useMutationState({
    filters: { mutationKey: [...mutationKey] },
    select: (mutation) => mutation.state,
  });
  const last = states.at(-1);
  if (last?.status !== "error") return null;
  const vars = last.variables as (TVars & { name?: string }) | undefined;
  return { error: last.error, name: vars?.name ?? "" };
}

// ---- roles ---------------------------------------------------------------------

export const rolesKey = (slug: string) => ["ws", slug, "roles"] as const;

/** Roles, highest first. */
export const rolesQuery = (api: Api, slug: string) =>
  queryOptions({
    queryKey: rolesKey(slug),
    queryFn: async () =>
      (await api<{ roles: RoleView[] }>(`${ws(slug)}/roles`)).roles,
  });

export interface RoleInput {
  name: string;
  color?: string | null;
  permissions?: string[];
}

export function useCreateRole(slug: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RoleInput) =>
      api<RoleView>(`${ws(slug)}/roles`, {
        method: "POST",
        body: input,
        idempotencyKey: idempotencyKeyFor(input),
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: rolesKey(slug) }),
  });
}

export interface RolePatch {
  name?: string;
  color?: string | null;
  position?: number;
  permissions?: string[];
}

export function useUpdateRole(slug: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { roleId: string; patch: RolePatch }) =>
      api<RoleView>(`${ws(slug)}/roles/${enc(input.roleId)}`, {
        method: "PATCH",
        body: input.patch,
      }),
    ...optimisticMutation<RoleView[], { roleId: string; patch: RolePatch }>(
      qc,
      rolesKey(slug),
      (old, { roleId, patch }) =>
        old
          ?.map((r) => (r.id === roleId ? { ...r, ...patch } : r))
          .sort((a, b) => b.position - a.position),
    ),
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: rolesKey(slug) }),
        qc.invalidateQueries({ queryKey: workspaceViewKey(slug) }),
      ]),
  });
}

const deleteRoleKey = (slug: string) => ["ws", slug, "delete-role"] as const;

export const useDeleteRoleFailure = (slug: string) =>
  useLastFailure<{ name: string }>(deleteRoleKey(slug));

export function useDeleteRole(slug: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationKey: deleteRoleKey(slug),
    mutationFn: (input: { roleId: string; name: string }) =>
      api(`${ws(slug)}/roles/${enc(input.roleId)}`, { method: "DELETE" }),
    ...optimisticMutation<RoleView[], { roleId: string }>(
      qc,
      rolesKey(slug),
      (old, { roleId }) => old?.filter((r) => r.id !== roleId),
    ),
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: rolesKey(slug) }),
        qc.invalidateQueries({ queryKey: membersKey(slug) }),
        // The invites that gave the role go with it.
        qc.invalidateQueries({ queryKey: invitesKey(slug) }),
      ]),
  });
}

// ---- invites -------------------------------------------------------------------

export const invitesKey = (slug: string) => ["ws", slug, "invites"] as const;

/** Invite links, newest first. */
export const invitesQuery = (api: Api, slug: string) =>
  queryOptions({
    queryKey: invitesKey(slug),
    queryFn: async () =>
      (await api<{ invites: InviteView[] }>(`${ws(slug)}/invites`)).invites,
  });

export interface InviteInput {
  roleId?: string;
  memberKind?: MemberKind;
  maxUses?: number;
  expiresAt?: string;
}

export function useCreateInvite(slug: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteInput) =>
      api<InviteView>(`${ws(slug)}/invites`, {
        method: "POST",
        body: input,
        idempotencyKey: idempotencyKeyFor(input),
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: invitesKey(slug) }),
  });
}

export function useRevokeInvite(slug: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { inviteId: string }) =>
      api(`${ws(slug)}/invites/${enc(input.inviteId)}`, { method: "DELETE" }),
    ...optimisticMutation<InviteView[], { inviteId: string }>(
      qc,
      invitesKey(slug),
      (old, { inviteId }) =>
        old?.map((i) =>
          i.id === inviteId
            ? { ...i, revokedAt: new Date().toISOString(), valid: false }
            : i,
        ),
    ),
  });
}

// ---- spaces --------------------------------------------------------------------

export const spacesKey = (slug: string) => ["ws", slug, "spaces"] as const;

/** The spaces the person sees, in the person's order. */
export const spacesQuery = (api: Api, slug: string) =>
  queryOptions({
    queryKey: spacesKey(slug),
    queryFn: async () =>
      (await api<{ spaces: SpaceView[] }>(`${ws(slug)}/spaces`)).spaces,
  });

export interface SpaceInput {
  name: string;
  slug?: string;
  description?: string;
  visibility?: SpaceVisibility;
  features?: string[];
}

export function useCreateSpace(slug: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SpaceInput) =>
      api<SpaceView>(`${ws(slug)}/spaces`, {
        method: "POST",
        body: input,
        idempotencyKey: idempotencyKeyFor(input),
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: spacesKey(slug) }),
  });
}

export interface SpacePatch {
  name?: string;
  slug?: string;
  description?: string;
  visibility?: SpaceVisibility;
  features?: string[];
  archived?: boolean;
}

export function useUpdateSpace(slug: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { spaceId: string; patch: SpacePatch }) =>
      api<SpaceView>(`${ws(slug)}/spaces/${enc(input.spaceId)}`, {
        method: "PATCH",
        body: input.patch,
      }),
    ...optimisticMutation<SpaceView[], { spaceId: string; patch: SpacePatch }>(
      qc,
      spacesKey(slug),
      (old, { spaceId, patch }) =>
        old?.map((s) => {
          if (s.id !== spaceId) return s;
          const { archived, ...rest } = patch;
          return {
            ...s,
            ...rest,
            ...(archived === undefined
              ? {}
              : { archivedAt: archived ? new Date().toISOString() : null }),
          };
        }),
    ),
    onSettled: (_data, _error, input) =>
      Promise.all([
        qc.invalidateQueries({ queryKey: spacesKey(slug) }),
        // Making a space private adds its managers and the one who changed it.
        qc.invalidateQueries({
          queryKey: spacePeopleKey(slug, "members", input.spaceId),
        }),
      ]),
  });
}

const deleteSpaceKey = (slug: string) => ["ws", slug, "delete-space"] as const;

export const useDeleteSpaceFailure = (slug: string) =>
  useLastFailure<{ name: string }>(deleteSpaceKey(slug));

export function useDeleteSpace(slug: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationKey: deleteSpaceKey(slug),
    mutationFn: (input: { spaceId: string; name: string }) =>
      api(`${ws(slug)}/spaces/${enc(input.spaceId)}`, { method: "DELETE" }),
    ...optimisticMutation<SpaceView[], { spaceId: string }>(
      qc,
      spacesKey(slug),
      (old, { spaceId }) => old?.filter((s) => s.id !== spaceId),
    ),
  });
}

export type SpacePeople = "members" | "managers";

export const spacePeoplePrefix = (slug: string) =>
  ["ws", slug, "space-people"] as const;
export const spacePeopleKey = (
  slug: string,
  which: SpacePeople,
  spaceId: string,
) => ["ws", slug, "space-people", which, spaceId] as const;

/** A space's members or managers. */
export const spacePeopleQuery = (
  api: Api,
  slug: string,
  which: SpacePeople,
  spaceId: string,
) =>
  queryOptions({
    queryKey: spacePeopleKey(slug, which, spaceId),
    queryFn: async () =>
      (
        await api<{ members: SpaceMemberView[] }>(
          `${ws(slug)}/spaces/${enc(spaceId)}/${which}`,
        )
      ).members,
  });

export function useAddSpacePerson(
  slug: string,
  which: SpacePeople,
  spaceId: string,
) {
  const api = useApi();
  const qc = useQueryClient();
  const key = spacePeopleKey(slug, which, spaceId);
  return useMutation({
    mutationFn: (input: { person: SpaceMemberView }) =>
      api<{ members: SpaceMemberView[] }>(
        `${ws(slug)}/spaces/${enc(spaceId)}/${which}`,
        { method: "POST", body: { userId: input.person.userId } },
      ),
    ...optimisticMutation<SpaceMemberView[], { person: SpaceMemberView }>(
      qc,
      key,
      (old, { person }) =>
        old?.some((p) => p.userId === person.userId)
          ? old
          : [...(old ?? []), person],
    ),
    onSuccess: (answer) => qc.setQueryData(key, answer.members),
    onSettled: () => settleSpacePeople(qc, slug),
  });
}

export function useRemoveSpacePerson(
  slug: string,
  which: SpacePeople,
  spaceId: string,
) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; name: string }) =>
      api(`${ws(slug)}/spaces/${enc(spaceId)}/${which}/${enc(input.userId)}`, {
        method: "DELETE",
      }),
    ...optimisticMutation<SpaceMemberView[], { userId: string }>(
      qc,
      spacePeopleKey(slug, which, spaceId),
      (old, { userId }) => old?.filter((p) => p.userId !== userId),
    ),
    onSettled: () => settleSpacePeople(qc, slug),
  });
}

/** Joining or leaving changes what the person sees (joined, private spaces). */
const settleSpacePeople = (qc: QueryClient, slug: string) =>
  Promise.all([
    qc.invalidateQueries({ queryKey: spacePeoplePrefix(slug) }),
    qc.invalidateQueries({ queryKey: spacesKey(slug) }),
  ]);
